import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SuppliesMainGroupName } from '../../../../../models/suppliesMain';
import { SuppliesDivisionModel } from '../../../../../models/suppliesDivisionModel';
import { generateImageFileNameWithRandomName } from '../../../../../utils/generateImageFileName';
import { MASTER_DIVISION_CREATE, MASTER_DIVISION_UPDATE } from '../../../../../lib/endpoints/category';
import axios from '../../../../../lib/axios';
import { IMAGE_CATEGORY_ROOT_URL } from '../../../../../constants/imageUrl';


interface Props {
  onOpen: boolean;
  onClose: VoidFunction;
  mainId: SuppliesMainGroupName[];
  selectedDiv: SuppliesDivisionModel;
  onfetch: VoidFunction;
  selected: Function;
}

export const useFormDivisionController = ({
  mainId,
  selectedDiv,
  onOpen,
  onClose,
  onfetch,
  selected,
}: Props) => {
  const [imageToShow, setImageToShow] = useState<any>(null!);
  const [imageFile, setImageFile] = useState<any>(null!);
  const [openConfirmForm, setOpenConfirmForm] = useState<boolean>(false);
  const [loadingConfirmButton, setLoadingConfirmButton] = useState<boolean>(false);
  const [imageError, setImageError] = useState<string>(null!);
  const [errorMessage, setErrorMessage] = useState<string>(null!);
  const divCode = useRef<HTMLInputElement>(null!);
  const nameLa = useRef<HTMLInputElement>(null!);
  const nameEn = useRef<HTMLInputElement>(null!);

  const { t } = useTranslation('global');

  //==========================> Open Event
  const handleOpenForm = (e: any) => {
    e.preventDefault();
    if (handleValidateForm()) {
      setOpenConfirmForm(true);
    }
  };

  const handleCloseConfirmForm = () => {
    setOpenConfirmForm(false);
  };

  //==========================> Function
  const handleValidateForm = () => {
    if (!selectedDiv && !imageFile) {
      setImageError('Please selected Image.');
      return false;
    }
    if (imageFile && imageError) {
      setImageError('');
    }
    setErrorMessage('');
    return true;
  };

  const handleReset = () => {
    selected(null);
    setImageToShow(null);
    setImageFile(null);
    divCode.current.value = '';
    nameLa.current.value = '';
    nameEn.current.value = '';
  };

  const handleSelectImage = (e: any) => {
    const file = e.target.files[0];
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.readyState === 2) {
        setImageToShow(reader.result);
      }
    };
    reader.readAsDataURL(file);
    setImageToShow(file);
  };

  const handleSubmit = () => {
    if (selectedDiv?.id) {
      handleEditDivision();
    } else {
      handleAddDivision();
    }
  };

  const handleAddDivision = async () => {
    try {
      setLoadingConfirmButton(true);
      const FormInput = {
        code: divCode.current?.value,
        division_name_la: nameLa.current?.value,
        division_name_en: nameEn.current?.value,
        supplies_main_group_name_id: mainID,
        file: generateImageFileNameWithRandomName(imageFile),
      };
      await axios.post(`${MASTER_DIVISION_CREATE}`, FormInput, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      onClose();
      await onfetch();
      handleReset();
    } catch (error: any) {
      try {
        const getErrorMessage: string = error.response.data.message;
        setErrorMessage(getErrorMessage);
      } catch (err) {
        setErrorMessage(t('Server error please try again') || '');
      }
    } finally {
      setLoadingConfirmButton(false);
      setOpenConfirmForm(false);
    }
  };

  const handleEditDivision = async () => {
    try {
      setLoadingConfirmButton(true);
      const FormInput: any = {
        code: divCode.current?.value,
        division_name_la: nameLa.current?.value,
        division_name_en: nameEn.current?.value,
        supplies_main_group_name_id: mainID,
      };
      if (imageFile) {
        FormInput.file = generateImageFileNameWithRandomName(imageFile);
      }
      await axios.patch(`${MASTER_DIVISION_UPDATE + selectedDiv.id}`, FormInput, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: 'multipart/form-data',
        },
      });
      onClose();
      await onfetch();
      handleReset();
    } catch (error: any) {
      try {
        const getErrorMessage: string = error.response.data.message;
        setErrorMessage(getErrorMessage);
      } catch (err) {
        setErrorMessage(t('Server error please try again') || '');
      }
    } finally {
      setLoadingConfirmButton(false);
      setOpenConfirmForm(false);
      onfetch();
    }
  };

  //==============================> filter Event

  const filterMainId = mainId.filter((item) => item.id);
  const getMain = filterMainId.length > 0 ? filterMainId[0] : null;
  const mainID = getMain ? getMain.id : null;

  //=================================> useEffect
  useEffect(() => {
    if (selectedDiv) {
      divCode.current.value = selectedDiv?.code;
      nameLa.current.value = selectedDiv?.division_name_la;
      nameEn.current.value = selectedDiv?.division_name_en;
      setImageToShow(IMAGE_CATEGORY_ROOT_URL + selectedDiv?.icon?.url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDiv]);

  return {
    handleSelectImage,
    imageToShow,
    handleOpenForm,
    handleCloseConfirmForm,
    divCode,
    nameLa,
    nameEn,
    errorMessage,
    imageError,
    openConfirmForm,
    loadingConfirmButton,
    handleSubmit,
    handleReset,
  };
};
